import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';

import { readingControlsStatusBoxSx } from './ReadingControls.styles';

interface Props {
  hasShuffled: boolean;
  selectedCount: number;
  requiredCards: number;
  onShuffle: () => void;
  onComplete: () => void;
}

function ReadingControls({
  hasShuffled,
  selectedCount,
  requiredCards,
  onShuffle,
  onComplete,
}: Props) {
  const canComplete = selectedCount === requiredCards;

  // The confirmation prompt opens automatically when the selection is complete.
  // "Not yet" dismisses it; it re-arms as soon as the selection becomes incomplete again.
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [wasCompletable, setWasCompletable] = useState(canComplete);
  if (wasCompletable !== canComplete) {
    setWasCompletable(canComplete);
    if (!canComplete) setPromptDismissed(false);
  }
  const showPrompt = hasShuffled && canComplete && !promptDismissed;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
      <Button
        variant="contained"
        size="large"
        startIcon={<ShuffleIcon />}
        onClick={onShuffle}
        aria-label="Shuffle the tarot deck"
      >
        Shuffle Deck
      </Button>

      {hasShuffled && (
        <Box sx={readingControlsStatusBoxSx}>
          <Typography variant="body2" color="primary" aria-live="polite">
            {selectedCount} / {requiredCards} cards selected
          </Typography>
          <Button
            variant="contained"
            disabled={!canComplete}
            onClick={onComplete}
            endIcon={<CheckCircleIcon />}
            aria-label="Complete reading with selected cards"
          >
            Complete Reading
          </Button>
        </Box>
      )}

      <Dialog
        open={showPrompt}
        onClose={() => setPromptDismissed(true)}
        aria-labelledby="complete-reading-prompt"
      >
        <DialogContent>
          <DialogContentText id="complete-reading-prompt">
            All {requiredCards} cards are selected. Reveal your reading now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDismissed(true)}>Not yet</Button>
          <Button variant="contained" onClick={onComplete} autoFocus>
            Reveal reading
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default ReadingControls;
